import { Action } from "redux";

import { TypedObject } from "@multiweb/types/react";
import { CLEAR_ENUM_DATA, LOAD_ENUMS_DATA, SET_ENUMS_DATA } from "./constants";

export type EnumsInstances = TypedObject<EnumInstance>;

/**
 * Struktura dat pro jeden enum.
 * K datům může v jeden moment přistupovat více komponent. Tento počet je udržován v proměnné `counter`.
 *
 * Pozn. Zatím stav není normalizován. V případě potřeby by se měly optimalizovat descriptions z enumType.descriptions
 * podle name a intValue. Tzn. přidat sem TypedObjects podobně jako u EnumsInstances.
 */
export type EnumInstance = Readonly<{
    readonly enumType: IReduxEnumType | null;
    readonly counter: number;
}>;

/**
 * Pomocný typ pro akci `ISetEnumsAction`, který obsahuje jak enum data, tak klíč, pod kterým budou v reduxu uložena.
 */
export type EnumTypeWithKey = Readonly<{
    readonly enumType: IReduxEnumType;
    readonly enumKey: string;
}>;

/**
 * Rozhraní obsahující detailní informace o enumu.
 */
export interface IReduxEnumType {

    /**
     * Doplňující údaje k enumu.
     */
    readonly descriptions: ReadonlyArray<IReduxEnumDescription>;

    /**
     * Typ enumu. Obvykle se používá k držení údaje o názvu enumu.
     */
    readonly type: string;
}

/**
 * Rozhraní obsahující doplňující informace k položce enumu.
 */
export interface IReduxEnumDescription {
    /**
     * Pořadové číslo položky enumu
     */
    readonly intValue: number;

    /**
     * Název položky enumu
     */
    readonly name: string;

    /**
     * Lokalizační klíč pro položku enumu
     */
    readonly locKey: string;

    /**
     * Popis položky enumu
     */
    readonly description: string;
}

/**
 * Definice stavu pro enums.
 */
export interface IEnumsState {
    readonly enumsInstances: EnumsInstances;
}

/**
 * Klíč pro jednoznačnou identifikaci enumu. Na základě jednoho klíče je možné přistupovat ke stavu z více komponent.
 */
export type EnumKey = string;

/**
 * Indikuje počátek načítání dat týkajících se enumů. Data jsou načítána na základě jmen typů enumu.
 */
export interface ILoadEnumsAction extends Action<typeof LOAD_ENUMS_DATA> {
    readonly payload: {
        readonly enumsKeys: ReadonlyArray<EnumKey>;
        readonly enableBatching: boolean;
    };
}

export class ILoadEnumsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_ENUMS_DATA;
    }
}

/**
 * Vložení dat týkajících se enumu do stavu, kde klíč pro identifikaci enumu je jeho jméno (`enumName`).
 */
export interface ISetEnumsAction extends Action<typeof SET_ENUMS_DATA> {
    readonly payload: {
        readonly enumTypesWithKeys: ReadonlyArray<EnumTypeWithKey>;
    };
}

export class ISetEnumsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_ENUMS_DATA;
    }
}

/**
 * Parametry pro selektory k vyhledání hodnoty enumu.
 */
export type EnumValueSelectorParams = {
    readonly enumTypeName: string;
    readonly value: string | number;
};

/**
 * Akce využívaná pro vymazání dat enumu v momentě, kdy už je komponenta nepotřebuje.
 */
export interface IClearEnumAction extends Action<typeof CLEAR_ENUM_DATA> {
    readonly payload: {
        readonly enumsKey: EnumKey;
    };
}

export class IClearEnumAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_ENUM_DATA;
    }
}
